import { StateService } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EMPTY, catchError, finalize, take } from "rxjs";
import { localize } from "@gtmhub/localization";
import { getCurrentUserId } from "@gtmhub/users";
import { Comment, CommentTargetType, CommentsFacade } from "@webapp/comments";
import { ErrorObject, createErrorObject } from "@webapp/error-handling/error-util";
import dayjs from "@webapp/shared/libs/dayjs";
import { cancelButton, primaryButton } from "@webapp/shared/modal/modal.utils";
import { UiModalService } from "@webapp/ui/modal/services/modal.service";
import { CHART_POINT_DATE_FORMAT } from "../../kpi-details/kpi-chart/utils";
import { KpiDetailsStateProxyService } from "../../kpi-details/services/kpi-details-state-proxy.service";
import { KpiCommentFormComponent, KpiCommentFormModalData } from "../kpi-comment-form/kpi-comment-form.component";
import { LeadingComment, LeadingCommentData } from "../models";

/** the targetTitle field is omitted because the comments in this screen cannot be modified at the moment.
 * if we want it in the future, include it here as well as in the target input of the comment component */
const COMMENTS_REQUEST_FIELDS: (keyof Comment)[] = [
  "id",
  "createdAt",
  "createdBy",
  "editedBy",
  "mentioned",
  "seen",
  "targetDate",
  "targetId",
  "targetType",
  "text",
  "gif",
];

@UntilDestroy()
@Component({
  selector: "kpi-comments",
  templateUrl: "kpi-comments.component.html",
  styleUrls: ["kpi-comments.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCommentsComponent implements OnInit {
  @Input() public kpiId: string;

  public leadingComments: LeadingComment[];
  public isLoading: boolean;
  public error: ErrorObject;

  constructor(
    private commentsFacade: CommentsFacade,
    private kpiDetailsStateProxyService: KpiDetailsStateProxyService,
    private changeDetector: ChangeDetectorRef,
    private stateService: StateService,
    private modalService: UiModalService
  ) {}

  public ngOnInit(): void {
    this.isLoading = true;
    const filter = { fields: COMMENTS_REQUEST_FIELDS };
    const queryParams = {
      targetIds: [this.kpiId],
      // we are fetching the comments 'first created first incoming' so we can easily populate the leading comments below
      sort: "createdAt",
    };

    this.commentsFacade
      .getComments$(filter, queryParams)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.error = createErrorObject(error);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
          this.changeDetector.markForCheck();
        })
      )
      .subscribe((comments) => {
        this.processComments(comments);
      });

    this.handleProjectionCommentDeletion();
  }

  public openCommentForm(): void {
    this.kpiDetailsStateProxyService
      .getCommentFormData$()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((formData) => {
        this.modalService.create<KpiCommentFormComponent, KpiCommentFormModalData>({
          uiTitle: localize("add_comment"),
          uiContent: KpiCommentFormComponent,
          uiData: formData,
          uiWidth: 500,
          uiFooter: [
            primaryButton(localize("add_comment"), {
              disabled: (kpiFormComponent) => !kpiFormComponent.kpiCommentForm.valid || kpiFormComponent.isCreatingComment,
              onClick: (kpiFormComponent) => kpiFormComponent.createComment(),
            }),
            cancelButton({
              disabled: (kpiFormComponent) => kpiFormComponent.isCreatingComment,
            }),
          ],
        });
      });
  }

  private handleProjectionCommentDeletion(): void {
    this.kpiDetailsStateProxyService
      .getProjectionDeleted$()
      .pipe(untilDestroyed(this))
      .subscribe((deletedProjectionDate) => {
        this.leadingComments = this.filterActiveLeadingComments(deletedProjectionDate);

        if (this.leadingComments.length === 0) {
          this.stateService.go("^");
        } else {
          this.changeDetector.detectChanges();
        }
      });
  }

  private filterActiveLeadingComments(deletedProjectionDate: string): LeadingComment[] {
    return this.leadingComments.filter(({ targetType, targetDate }) => this.isNotDeletedProjectionComment(targetType, targetDate, deletedProjectionDate));
  }

  private isNotDeletedProjectionComment(targetType: CommentTargetType, targetDate: string, deletedProjectionDate: string): boolean {
    const isProjectionTargetType = targetType === "kpi_projection";
    const formattedTargetDate = dayjs(targetDate).format(CHART_POINT_DATE_FORMAT);
    const formattedDeletedProjectionDate = dayjs(deletedProjectionDate).format(CHART_POINT_DATE_FORMAT);

    return isProjectionTargetType && formattedTargetDate !== formattedDeletedProjectionDate;
  }

  private processComments(fetchedComments: Comment[]): void {
    const leadingComments: LeadingComment[] = [];
    const leadingCommentsMap = new Map<string, LeadingComment>();
    const currentUserId = getCurrentUserId();

    // API sorting guarantees us that the first comment for a given entity is the leading comment, the last comment is the last reply
    fetchedComments.forEach((comment) => {
      const { targetDate, targetType, createdAt, createdBy, seen, mentioned } = comment;
      // comments API allows the creation of comments with target type "kpi" but we don't want to show them
      if (targetType === "kpi") {
        return;
      }

      const targetEntityMapKey = JSON.stringify({ targetDate, targetType });
      const existingLeadingCommentForEntity = leadingCommentsMap.get(targetEntityMapKey);

      if (existingLeadingCommentForEntity) {
        existingLeadingCommentForEntity.repliesCount++;
        existingLeadingCommentForEntity.lastReplyAt = createdAt;
        existingLeadingCommentForEntity.usersReplied.add(createdBy);
      } else {
        const leadingComment = { ...comment, ...this.newLeadingCommentData, formattedTargetDate: dayjs(targetDate).format(CHART_POINT_DATE_FORMAT) };
        leadingCommentsMap.set(targetEntityMapKey, leadingComment);
        leadingComments.push(leadingComment);
      }

      const hasCurrentUserSeenTheComment = seen?.includes(currentUserId);
      if (hasCurrentUserSeenTheComment) {
        return;
      }

      const leadingCommentTheCurrentOneBelongsTo = leadingCommentsMap.get(targetEntityMapKey);
      const isCurrentUserMentioned = mentioned?.includes(currentUserId);
      const isFollowingAnUnreadComment = leadingCommentTheCurrentOneBelongsTo.unreadCommentsCount > 0;
      const shouldCountTheCommentAsUnread = isCurrentUserMentioned || isFollowingAnUnreadComment;
      if (shouldCountTheCommentAsUnread) {
        leadingCommentTheCurrentOneBelongsTo.unreadCommentsCount++;
      }
    });

    // the comments are displayed in a 'latest at the top' fashion, so we reverse
    leadingComments.reverse();
    this.leadingComments = leadingComments;
  }

  private get newLeadingCommentData(): Omit<LeadingCommentData, "formattedTargetDate"> {
    return {
      repliesCount: 0,
      unreadCommentsCount: 0,
      usersReplied: new Set<string>(),
      lastReplyAt: null,
    };
  }
}
